import { useEffect, useState } from "react";
import {
  useGetAppConfigQuery,
  useGetClientVersionCeilingsQuery,
  useUpdateAppConfigMutation,
} from "../../redux/api";
import { Field } from "../../components/Field";

const PLATFORM_LABELS: Record<string, string> = {
  android: "Android",
  ios: "iOS",
  web: "Web app",
};

/**
 * The version table's rows, in display order. Minimum and latest used to be two
 * separate three-input grids with the ceiling evidence in a list below them —
 * three places to look for one platform's story. One row per platform puts the
 * floor, the declared release, and what is actually running side by side, which
 * is the comparison the operator is actually making.
 *
 * `minErrorKey` matches the key handleSave writes into `fieldErrors`.
 */
const VERSION_ROWS = [
  { platform: "android", minErrorKey: "androidMinVersionCode" },
  { platform: "ios", minErrorKey: "iosMinVersionCode" },
  { platform: "web", minErrorKey: "webappMinVersionCode" },
] as const;

// Platform Owner → Settings → App Configuration. Platform-wide settings that
// aren't tied to any one organization (e.g. how long trashed records are kept
// before they're auto-purged).
const AppConfigScreen = () => {
  const { data: config, isLoading } = useGetAppConfigQuery();
  const [updateAppConfig, { isLoading: saving }] = useUpdateAppConfigMutation();
  // Evidence for the minimum-version inputs: what is genuinely running out
  // there. Shown rather than merely enforced, so the number is chosen with the
  // consequence in view instead of being rejected after the fact.
  const { data: ceilings = [] } = useGetClientVersionCeilingsQuery();

  const [trashRetentionDays, setTrashRetentionDays] = useState("30");
  const [accountDeletionGraceDays, setAccountDeletionGraceDays] = useState("30");
  const [androidMinVersionCode, setAndroidMinVersionCode] = useState("0");
  const [iosMinVersionCode, setIosMinVersionCode] = useState("0");
  const [webappMinVersionCode, setWebappMinVersionCode] = useState("0");
  const [androidLatestVersionCode, setAndroidLatestVersionCode] = useState("0");
  const [iosLatestVersionCode, setIosLatestVersionCode] = useState("0");
  const [webappLatestVersionCode, setWebappLatestVersionCode] = useState("0");
  const [updateMessage, setUpdateMessage] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  // Per-field validation messages — shown directly under the offending input
  // (with a red border) instead of the single top-of-form banner.
  const [fieldErrors, setFieldErrors] = useState<Record<string, string | undefined>>({});

  useEffect(() => {
    if (config) {
      setTrashRetentionDays(String(config.trashRetentionDays));
      setAccountDeletionGraceDays(String(config.accountDeletionGraceDays));
      setAndroidMinVersionCode(String(config.androidMinVersionCode));
      setIosMinVersionCode(String(config.iosMinVersionCode));
      setWebappMinVersionCode(String(config.webappMinVersionCode));
      setAndroidLatestVersionCode(String(config.androidLatestVersionCode));
      setIosLatestVersionCode(String(config.iosLatestVersionCode));
      setWebappLatestVersionCode(String(config.webappLatestVersionCode));
      setUpdateMessage(config.updateMessage ?? "");
    }
  }, [config?.id, config?.updatedAt]);

  // Bridges the flat useState pairs above to the table's row-driven render.
  // Kept as a lookup rather than folding the state into VERSION_ROWS itself:
  // hooks cannot be created from a loop over a config array, and collapsing
  // eight useState calls into one object would rewrite far more of this screen
  // than a table warrants.
  const versionState: Record<
    (typeof VERSION_ROWS)[number]["platform"],
    { min: string; setMin: (v: string) => void; latest: string; setLatest: (v: string) => void }
  > = {
    android: {
      min: androidMinVersionCode,
      setMin: setAndroidMinVersionCode,
      latest: androidLatestVersionCode,
      setLatest: setAndroidLatestVersionCode,
    },
    ios: {
      min: iosMinVersionCode,
      setMin: setIosMinVersionCode,
      latest: iosLatestVersionCode,
      setLatest: setIosLatestVersionCode,
    },
    web: {
      min: webappMinVersionCode,
      setMin: setWebappMinVersionCode,
      latest: webappLatestVersionCode,
      setLatest: setWebappLatestVersionCode,
    },
  };

  const dirty =
    config != null &&
    (trashRetentionDays !== String(config.trashRetentionDays) ||
      accountDeletionGraceDays !== String(config.accountDeletionGraceDays) ||
      androidMinVersionCode !== String(config.androidMinVersionCode) ||
      iosMinVersionCode !== String(config.iosMinVersionCode) ||
      webappMinVersionCode !== String(config.webappMinVersionCode) ||
      androidLatestVersionCode !== String(config.androidLatestVersionCode) ||
      iosLatestVersionCode !== String(config.iosLatestVersionCode) ||
      webappLatestVersionCode !== String(config.webappLatestVersionCode) ||
      updateMessage !== (config.updateMessage ?? ""));

  const handleSave = async () => {
    setError(null);
    setSaved(false);

    const trashDays = Number(trashRetentionDays);
    const deletionDays = Number(accountDeletionGraceDays);
    const androidMin = Number(androidMinVersionCode);
    const iosMin = Number(iosMinVersionCode);
    const webappMin = Number(webappMinVersionCode);
    const errors: Record<string, string> = {};
    if (!Number.isFinite(trashDays) || trashDays < 1) {
      errors.trashRetentionDays = "Trash retention must be at least 1 day.";
    }
    if (!Number.isFinite(deletionDays) || deletionDays < 1) {
      errors.accountDeletionGraceDays = "Grace period must be at least 1 day.";
    }
    // 0 disables the gate, so it is a legitimate value — only reject negatives
    // and non-numbers here. The dangerous case (a floor higher than anything
    // users can actually run) is checked by the server against real observed
    // traffic and confirmed below, rather than guessed at in the browser.
    if (!Number.isFinite(androidMin) || androidMin < 0) {
      errors.androidMinVersionCode = "Must be 0 or a positive version code.";
    }
    if (!Number.isFinite(iosMin) || iosMin < 0) {
      errors.iosMinVersionCode = "Must be 0 or a positive version code.";
    }
    if (!Number.isFinite(webappMin) || webappMin < 0) {
      errors.webappMinVersionCode = "Must be 0 or a positive version code.";
    }
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }
    setFieldErrors({});

    const payload = {
      trashRetentionDays: trashDays,
      accountDeletionGraceDays: deletionDays,
      androidMinVersionCode: androidMin,
      iosMinVersionCode: iosMin,
      webappMinVersionCode: webappMin,
      androidLatestVersionCode: Number(androidLatestVersionCode) || 0,
      iosLatestVersionCode: Number(iosLatestVersionCode) || 0,
      webappLatestVersionCode: Number(webappLatestVersionCode) || 0,
      updateMessage: updateMessage.trim(),
    };

    try {
      await updateAppConfig(payload).unwrap();
      setSaved(true);
    } catch (err) {
      const message =
        (err as { message?: string })?.message ?? "Failed to save app configuration.";

      // The server refuses a floor no client in the wild can satisfy. That is
      // a guard, not a hard rule — deliberately pulling a broken release is a
      // real case — so surface exactly what it will cost and let the operator
      // decide, rather than silently retrying or silently giving up.
      if (message.includes("Re-submit with force")) {
        if (!window.confirm(`${message}\n\nSet it anyway?`)) {
          setError("Save cancelled — minimum versions left unchanged.");
          return;
        }
        try {
          await updateAppConfig({ ...payload, force: true }).unwrap();
          setSaved(true);
          return;
        } catch (forcedErr) {
          setError(
            (forcedErr as { message?: string })?.message ??
              "Failed to save app configuration.",
          );
          return;
        }
      }

      setError(message);
    }
  };

  return (
    <div className="dashboard">
      <section className="panel">
        <div className="panel__head">
          <div>
            <h2>App Configuration</h2>
          </div>
        </div>
        <div className="panel__body">
          {error ? <div className="form-error">{error}</div> : null}
          {saved ? (
            <div
              className="form-error"
              style={{ background: "var(--success-soft)", color: "var(--success-strong)" }}
            >
              App configuration saved.
            </div>
          ) : null}

          {isLoading ? (
            <p>Loading…</p>
          ) : (
            <>
              <div className="form-grid">
                <Field label="Trash retention (days)" error={fieldErrors.trashRetentionDays}>
                  <input
                    type="number"
                    min={1}
                    value={trashRetentionDays}
                    onChange={(e) => {
                      setTrashRetentionDays(e.target.value);
                      setFieldErrors((prev) =>
                        prev.trashRetentionDays
                          ? { ...prev, trashRetentionDays: undefined }
                          : prev,
                      );
                    }}
                  />
                </Field>
                <Field
                  label="Account deletion grace period (days)"
                  error={fieldErrors.accountDeletionGraceDays}
                >
                  <input
                    type="number"
                    min={1}
                    value={accountDeletionGraceDays}
                    onChange={(e) => {
                      setAccountDeletionGraceDays(e.target.value);
                      setFieldErrors((prev) =>
                        prev.accountDeletionGraceDays
                          ? { ...prev, accountDeletionGraceDays: undefined }
                          : prev,
                      );
                    }}
                  />
                </Field>
              </div>
              <p className="panel__sub" style={{ marginTop: "0.5rem" }}>
                Trashed records older than the retention period are permanently
                deleted automatically (checked on boot, daily, and whenever the
                Trash screen is opened). The account deletion grace period is
                how long a self-service "Delete my account" request stays
                restorable before it's finalized.
              </p>

              <h3 style={{ marginTop: "1.5rem" }}>Client versions</h3>
              <div className="table-wrap">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Platform</th>
                      <th style={{ minWidth: 170 }}>Minimum required version</th>
                      <th style={{ minWidth: 170 }}>Latest released version</th>
                      <th style={{ minWidth: 260 }}>Info</th>
                    </tr>
                  </thead>
                  <tbody>
                    {VERSION_ROWS.map((row) => {
                      const min = versionState[row.platform];
                      const ceiling = ceilings.find((c) => c.platform === row.platform);
                      const observed = ceiling?.safeVersionCode ?? 0;

                      // Mirrors assertMinVersionsAreReachable exactly — a ceiling
                      // shown here that the server disagreed with would be worse
                      // than showing none. Web ignores the declared "latest" on
                      // purpose: a browser has no review queue, so what users can
                      // run is simply what is deployed, which the server cannot
                      // see and this form can only claim. Live traffic is the
                      // honest ceiling there.
                      const declaredLatest =
                        row.platform === "web" ? 0 : Number(min.latest) || 0;
                      const safe = Math.max(observed, declaredLatest);

                      return (
                        <tr key={row.platform}>
                          <td>
                            <strong>{PLATFORM_LABELS[row.platform]}</strong>
                          </td>
                          <td>
                            <div
                              className={`field${
                                fieldErrors[row.minErrorKey] ? " field--invalid" : ""
                              }`}
                            >
                              <input
                                type="number"
                                min={0}
                                aria-label={`${PLATFORM_LABELS[row.platform]} minimum version`}
                                value={min.min}
                                onChange={(e) => {
                                  min.setMin(e.target.value);
                                  setFieldErrors((prev) =>
                                    prev[row.minErrorKey]
                                      ? { ...prev, [row.minErrorKey]: undefined }
                                      : prev,
                                  );
                                }}
                              />
                              {fieldErrors[row.minErrorKey] ? (
                                <span className="field__error">
                                  {fieldErrors[row.minErrorKey]}
                                </span>
                              ) : null}
                            </div>
                          </td>
                          <td>
                            <div className="field">
                              <input
                                type="number"
                                min={0}
                                aria-label={`${PLATFORM_LABELS[row.platform]} latest version`}
                                value={min.latest}
                                onChange={(e) => min.setLatest(e.target.value)}
                              />
                            </div>
                          </td>
                          <td className="panel__sub" style={{ fontSize: "0.78rem" }}>
                            {safe > 0 ? (
                              <>
                                Floor can go up to <strong>{safe}</strong> without
                                confirming.
                              </>
                            ) : (
                              <>
                                Nothing recorded yet — any floor above 0 needs
                                confirming.
                              </>
                            )}
                            <br />
                            {observed > 0 ? (
                              <>
                                In use: {observed} ({ceiling?.userCount ?? 0} user
                                {ceiling?.userCount === 1 ? "" : "s"})
                              </>
                            ) : (
                              <>No installs seen yet</>
                            )}
                            {ceiling && ceiling.highestSeenVersionCode > observed ? (
                              <>
                                <br />
                                Highest seen: {ceiling.highestSeenVersionCode}
                              </>
                            ) : null}
                            {row.platform === "web" ? (
                              <>
                                <br />
                                <em>Ceiling ignores &ldquo;latest&rdquo; — live traffic only.</em>
                              </>
                            ) : null}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <p className="panel__sub" style={{ marginTop: "0.5rem" }}>
                <strong>Minimum required version</strong> is the kill switch:
                clients below it are blocked behind a full-screen &ldquo;Update
                required&rdquo; screen and cannot use the app, effective without
                shipping anything. <strong>0 disables it.</strong>{" "}
                <strong>Latest released version</strong> blocks nothing — the
                mobile app&rsquo;s &ldquo;Check for Update&rdquo; compares against
                it to answer &ldquo;you&rsquo;re up to date&rdquo; or offer the
                store, and <strong>0 means unknown</strong>. Version codes are the
                version with the dots removed (1.0.010 → 10010).
              </p>

              <Field label="Update message (optional)">
                <input
                  type="text"
                  value={updateMessage}
                  placeholder="Shown on the update screen, e.g. why the update is needed"
                  onChange={(e) => setUpdateMessage(e.target.value)}
                />
              </Field>
              <p className="panel__sub" style={{ marginTop: "0.5rem" }}>
                Shown on the update screen above the reload/store button. Set
                each minimum to the oldest client you still want working.{" "}
                <strong>
                  Never set a floor above what users can actually run
                </strong>{" "}
                — on mobile they have nothing to update to and stay locked out
                until a new release clears review, which takes days; on web,
                reloading only ever fetches the bundle that is actually
                deployed, so a floor above it puts every browser in a reload
                loop it cannot escape. The Info column shows how high each floor
                can safely go.
              </p>

              <div className="form-actions">
                <button
                  type="button"
                  className="btn btn--primary"
                  onClick={handleSave}
                  disabled={saving || !dirty}
                >
                  {saving ? "Saving…" : "Save changes"}
                </button>
              </div>
            </>
          )}
        </div>
      </section>
    </div>
  );
};

export default AppConfigScreen;
